import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useCustomer from "common/datahooks/useCustomer";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { EmailSchema } from "common/schemas";

import SettingsItemContainer from "../SettingsItemContainer";

export default function Email({
  isEditable,
}: {
  isEditable: boolean;
}): JSX.Element {
  const isDesktop = useTwBreakpoint("lg");

  const [isEditing, setIsEditing] = useState(false);
  const {
    updateCustomer,
    isCustomerUpdating,
    customerInfo,
    updateCustomerError,
    resetUpdateCustomer,
  } = useCustomer();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{
    email: string;
  }>({
    resolver: valibotResolver(EmailSchema),
    defaultValues: {
      email: customerInfo.email,
    },
  });

  async function onSubmit({ email }: { email: string }) {
    if (customerInfo.email !== email) {
      await updateCustomer({ email });
    }
    setIsEditing(false);
  }
  function onCancel() {
    setIsEditing(false);
    reset({ email: customerInfo.email });
  }

  let errorMessage: string;

  if (updateCustomerError) {
    if (
      updateCustomerError instanceof HTTPError &&
      updateCustomerError.type === "Conflict:"
    ) {
      errorMessage = "Email already exists";
    } else {
      errorMessage = "Error updating email, please try again later";
    }
  }
  return (
    <SettingsItemContainer className="items-center">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <FormField
            label="Email address"
            htmlFor="emailField"
            size={isDesktop ? "sm" : "lg"}
            className="flex-1"
            error={errorMessage || errors.email?.message}
          >
            <div className="flex flex-col lg:flex-row gap-2">
              <Input
                placeholder="New email address"
                id="emailField"
                variant={isDesktop ? "sm" : "lg"}
                className="lg:w-80"
                {...register("email", { onChange: resetUpdateCustomer })}
              />
              <div className="flex gap-x-2 mt-auto ml-auto">
                <Button
                  variant="secondary-black"
                  size={isDesktop ? "sm" : "md"}
                  onClick={onCancel}
                  disabled={isCustomerUpdating}
                >
                  Cancel
                </Button>
                <Button
                  size={isDesktop ? "sm" : "md"}
                  type="submit"
                  isLoading={isCustomerUpdating}
                >
                  Save
                </Button>
              </div>
            </div>
          </FormField>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-y-0.5">
            <span className="text-sm-bold">Email address</span>
            <span className="text-black-500 text-xs-medium">
              {customerInfo.email}
            </span>
          </div>
          <RenderIf condition={isEditable}>
            <Button
              variant="ghost-black"
              intent="iconOnly"
              className="ml-auto"
              onClick={() => setIsEditing(true)}
            >
              <PencilSimpleLine />
            </Button>
          </RenderIf>
        </>
      )}
    </SettingsItemContainer>
  );
}
